import { useState } from 'react';

import { Grid } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import { useAdminGetTaxes } from '../../hooks/admin-get-taxes.hook';
import { useCreateTax } from '../../hooks/create-tax.hook';
import { useUpdateTax } from '../../hooks/update-tax.hook';
import { useDeleteTax } from '../../hooks/delete-tax.hook';
import { TaxWithUsage } from '../../types/tax.type';
import { CreateTaxRequestDTO } from '../../types/dtos/create-tax.dto';
import { UpdateTaxRequestDTO } from '../../types/dtos/update-tax.dto';
import { TaxesTable } from '../../components/Tables/TaxesTable.component';
import { TaxDialog } from '../../components/Modals/TaxDialog.component';
import { CreateTaxCard } from '../../components/Forms/CreateTaxCard.component';
import { ConfirmActionDialog } from '../../../shared/components/Modals/ConfirmActionDialog.component';

type DialogState = { kind: 'none' } | { kind: 'edit'; tax: TaxWithUsage } | { kind: 'delete'; tax: TaxWithUsage };

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.7rem',

    '& > h2': {
      margin: 0,
    },

    '& h4': {
      margin: '0 0 0.5rem 0',
      opacity: 0.7,
    },

    '& .MuiButton-root': {
      textTransform: 'none',
    },

    '& .MuiOutlinedInput-root.Mui-focused:not(.Mui-error) .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.admin.primary.main,
    },
    '& .MuiInputLabel-outlined.Mui-focused:not(.Mui-error)': {
      color: theme.palette.admin.primary.main,
    },
  },
  listColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.7rem',
  },
}));

export default function AdminTaxesView() {
  const classes = useStyles();

  const { isLoading, isRefetching, taxes, refetch } = useAdminGetTaxes();
  const { isSubmitting: isCreating, createTax } = useCreateTax();
  const { isSubmitting: isUpdating, updateTax } = useUpdateTax();
  const { isSubmitting: isDeleting, deleteTax } = useDeleteTax();

  const [dialog, setDialog] = useState<DialogState>({ kind: 'none' });

  const closeDialog = () => setDialog({ kind: 'none' });

  const handleCreate = async (taxData: CreateTaxRequestDTO): Promise<boolean> => {
    const createdTax = await createTax(taxData);
    if (!createdTax) return false;

    await refetch();
    return true;
  };

  const handleEditSubmit = async (taxData: UpdateTaxRequestDTO) => {
    if (dialog.kind !== 'edit') return;

    const updatedTax = await updateTax(dialog.tax.id, taxData);
    if (!updatedTax) return;

    closeDialog();
    await refetch();
  };

  const handleDeleteConfirm = async () => {
    if (dialog.kind !== 'delete') return;

    const isDeleted = await deleteTax(dialog.tax.id);
    if (!isDeleted) return;

    closeDialog();
    await refetch();
  };

  return (
    <div className={classes.root}>
      <h2>Taxes</h2>
      <Grid container spacing={2}>
        <Grid item xs={12} md={8} className={classes.listColumn}>
          <TaxesTable
            taxes={taxes}
            isLoading={isLoading || isRefetching}
            onEdit={(tax) => setDialog({ kind: 'edit', tax })}
            onDelete={(tax) => setDialog({ kind: 'delete', tax })}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <h4>Créer une taxe</h4>
          <CreateTaxCard isSubmitting={isCreating} onCreate={handleCreate} />
        </Grid>
      </Grid>
      {dialog.kind === 'edit' && (
        <TaxDialog open tax={dialog.tax} isSubmitting={isUpdating} onSubmit={handleEditSubmit} onClose={closeDialog} />
      )}
      {dialog.kind === 'delete' && (
        <ConfirmActionDialog
          open
          title="Supprimer la taxe"
          description={`La taxe « ${dialog.tax.name} » sera définitivement supprimée.`}
          confirmLabel="Supprimer"
          isSubmitting={isDeleting}
          onConfirm={handleDeleteConfirm}
          onClose={closeDialog}
        />
      )}
    </div>
  );
}
